/**
 * FrameAgent — the widget's presence inside one iframe's document.
 *
 * Provides the frame's readable text (and tells the page when it changes),
 * highlights words while the page's player reads it, and reports text
 * selections and click-and-listen clicks so the page's player can read them.
 * It has no UI or player of its own.
 *
 * Same-origin frames: the page's widget runs an agent on the frame's document
 * directly. Other subdomains: the frame's own copy of the widget runs one in
 * frame mode and relays everything over postMessage (frameMode.ts).
 */
import type { WordHighlightInfo } from '../core/StreamingPlayer';
import { TextExtractor, readableSelectionText } from '../utils/textExtractor';
import { WordHighlighter } from '../ui/WordHighlighter';
import type { HighlightMode } from '../ui/highlightStyles';
import type { FrameRect, HighlightStyle } from './protocol';
import { DEFAULT_CONFIG } from '../types/config';

export interface FrameAgentEvents {
  /** The frame's readable text changed */
  text(text: string): void;
  /** Text selected in the frame (empty when the selection goes away) */
  selection(text: string, rect: FrameRect | null): void;
  /** Click-and-listen: read this text */
  speak(text: string): void;
}

const BLOCKS = 'p, li, h1, h2, h3, h4, h5, h6, blockquote, td, th, dd, dt, figcaption, pre';
const STYLE_ID = 'rv-frame-click-listen';
const ROOT_ATTR = 'data-rv-click-listen';

export class FrameAgent {
  readonly highlighter: WordHighlighter;
  private extractor: TextExtractor;
  private lastText = '';
  private hadSelection = false;
  private observer: MutationObserver | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private clickListenOn = false;

  constructor(
    readonly doc: Document,
    private events: FrameAgentEvents,
    opts: { autoScroll?: boolean; ignoreSelectors?: string[] } = {}
  ) {
    this.extractor = new TextExtractor([], opts.ignoreSelectors || DEFAULT_CONFIG.ignoreSelectors, { fallbackToBody: true, doc });
    this.highlighter = new WordHighlighter(opts.autoScroll !== false, doc.body ? [doc.body] : [], doc);

    doc.addEventListener('mouseup', this.onSelect);
    doc.addEventListener('keyup', this.onSelect);
    doc.addEventListener('touchend', this.onSelect);
    if (typeof MutationObserver !== 'undefined' && doc.body) {
      const MO = (doc.defaultView as (Window & typeof globalThis) | null)?.MutationObserver || MutationObserver;
      this.observer = new MO(() => this.scheduleTextCheck());
      this.observer.observe(doc.body, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['aria-hidden', 'hidden', 'style', 'class'] });
    }
    this.lastText = this.text();
  }

  /** Current readable text of the frame */
  text(): string {
    if (this.doc.body) this.highlighter.setContentRoots([this.doc.body]);
    return this.extractor.extract();
  }

  /** The text as last reported (cheap; kept current by the mutation observer) */
  get currentText(): string {
    return this.lastText;
  }

  highlightWord(info: WordHighlightInfo): boolean {
    return this.highlighter.highlightWord(info);
  }

  highlightSentence(sentence: string): boolean {
    return this.highlighter.highlightSentenceOnly(sentence);
  }

  clear(): void {
    this.highlighter.clearAll();
  }

  setStyle(style: HighlightStyle, autoScroll: boolean): void {
    this.highlighter.setStyle({ mode: style.mode as HighlightMode | undefined, word: style.word, sentence: style.sentence });
    this.highlighter.setAutoScroll(autoScroll);
  }

  clearSelection(): void {
    this.doc.getSelection()?.removeAllRanges();
    this.hadSelection = false;
  }

  setClickListen(on: boolean): void {
    if (on === this.clickListenOn) return;
    this.clickListenOn = on;
    const body = this.doc.body;
    if (on) {
      if (!this.doc.getElementById(STYLE_ID)) {
        const style = this.doc.createElement('style');
        style.id = STYLE_ID;
        style.textContent = `[${ROOT_ATTR}] :is(${BLOCKS}) { cursor: pointer; } [${ROOT_ATTR}] :is(${BLOCKS}):hover { outline: 2px dashed #2ec486; outline-offset: 3px; border-radius: 3px; }`;
        this.doc.head?.appendChild(style);
      }
      body?.setAttribute(ROOT_ATTR, '');
      this.doc.addEventListener('click', this.onClick, true);
    } else {
      body?.removeAttribute(ROOT_ATTR);
      this.doc.removeEventListener('click', this.onClick, true);
    }
  }

  destroy(): void {
    this.setClickListen(false);
    this.doc.removeEventListener('mouseup', this.onSelect);
    this.doc.removeEventListener('keyup', this.onSelect);
    this.doc.removeEventListener('touchend', this.onSelect);
    this.observer?.disconnect();
    if (this.timer) clearTimeout(this.timer);
    try { this.highlighter.destroy(); } catch { /* frame may be gone */ }
  }

  private scheduleTextCheck(): void {
    if (this.timer) return;
    this.timer = setTimeout(() => {
      this.timer = null;
      const text = this.text();
      if (text !== this.lastText) {
        this.lastText = text;
        this.events.text(text);
      }
    }, 400);
  }

  private onSelect = (): void => {
    setTimeout(() => {
      const sel = this.doc.getSelection();
      const raw = sel?.toString().trim() || '';
      if (!sel || raw.length < 2 || sel.rangeCount === 0) {
        if (this.hadSelection) {
          this.hadSelection = false;
          this.events.selection('', null);
        }
        return;
      }
      const text = readableSelectionText(sel);
      const r = sel.getRangeAt(0).getBoundingClientRect();
      this.hadSelection = true;
      this.events.selection(text, { left: r.left, top: r.top, width: r.width, height: r.height });
    }, 10);
  };

  /** Click-and-listen: read from the clicked block to the end of the frame */
  private onClick = (e: MouseEvent): void => {
    const target = e.target as Element | null;
    if (!target || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey) return;
    if (target.closest('a, button, input, select, textarea, label, summary, [contenteditable], [role="button"]')) return;
    if (this.doc.getSelection()?.toString().trim()) return;
    const start = target.closest(BLOCKS);
    if (!start || !this.doc.body) return;
    const blocks = Array.from(this.doc.body.querySelectorAll(BLOCKS)).filter((b) => !b.querySelector(BLOCKS));
    const i = blocks.findIndex((b) => b === start || b.contains(start) || start.contains(b));
    const chosen = i >= 0 ? blocks.slice(i) : [start];
    const text = chosen.map((b) => this.extractor.textOf(b)).filter((t) => t.trim()).join('\n\n');
    if (!text.trim()) return;
    e.preventDefault();
    e.stopPropagation();
    this.events.speak(text);
  };
}
